import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { ShieldCheck } from "lucide-react";

/**
 * CategorizerDisclosureModal (ARC-15)
 *
 * One-time acknowledgement gate in front of the AI Statement Categorizer.
 * The categorizer sends a member to a third-party AI service where they
 * upload their own bank statement — ARCrypto never receives the file, but
 * we are the ones pointing them there, so the disclosure ships first.
 *
 * Two modes:
 *   "gate"   — checkbox + Cancel / Continue. Continue is disabled until the
 *              box is ticked. Cancel / Escape / backdrop all decline.
 *   "review" — read-only reopen from the "Data & privacy notice" link. No
 *              checkbox, single Close button, nothing is persisted.
 *
 * The copy below is compliance-reviewed and reproduced verbatim from ARC-15.
 * Only the lead-in sentence of a paragraph may be emphasised; do not reword,
 * reorder, or condense. If the wording changes materially, bump
 * CATEGORIZER_DISCLOSURE_VERSION so every member re-acknowledges.
 */

/* ─── Version ─────────────────────────────────────────────────────────
   Compared against the member's stored categorizerDisclosureAcceptedVersion.
   A mismatch (or no stored value) means the gate shows again. */
export const CATEGORIZER_DISCLOSURE_VERSION = "1.0";

/* ─── Copy (verbatim — see the compliance note above) ─────────────── */

export const CATEGORIZER_DISCLOSURE_HEADING =
  "Before you use the AI Statement Categorizer";

// `lead` is the sentence we are permitted to emphasise; `rest` is the
// remainder of the same paragraph. A paragraph with no `rest` renders plain.
export const CATEGORIZER_DISCLOSURE_PARAGRAPHS = [
  {
    id: "intro",
    lead:
      "This tool sends you to an AI service you choose — Claude, ChatGPT, or Gemini. Here's exactly what happens to your data:",
  },
  {
    id: "never-sees",
    lead: "ARCrypto never sees your bank statement.",
    rest: "You upload it to your own AI account. It does not pass through our servers and we do not store it.",
  },
  {
    id: "provider",
    lead: "Your statement goes to the AI provider you pick.",
    rest: "Their privacy policy governs it, not ours. How long they keep it, and whether they use it to train their models, depends on that provider and your plan with them. Read their policy before you upload. We are not able to control or verify what they do with it.",
  },
  {
    id: "whose-account",
    lead: "Check whose account you're using.",
    rest: "If you're signed into a work or employer-managed AI account, your personal financial information may be visible to that organization. Use a personal account.",
  },
  {
    id: "what-we-store",
    lead: "What ARCrypto stores is the category totals you type into your tracker",
    rest: "— not individual transactions, merchants, or account numbers.",
  },
  {
    id: "account-numbers",
    lead: "Consider removing your account numbers from the file before uploading.",
    rest: "The categorizer only needs dates, descriptions, and amounts.",
  },
  {
    id: "not-advice",
    lead: "This is a bookkeeping tool, not financial advice.",
    rest: "It sorts your spending into categories. It does not tell you what to do with your money, and it is not tax advice. For decisions about your finances or taxes, talk to a qualified professional in your jurisdiction.",
  },
];

export const CATEGORIZER_DISCLOSURE_CHECKBOX_LABEL =
  "I understand and want to continue";

/* Paragraphs whose lead-in reads as a standalone statement, not a heading —
   emphasising these would over-weight the block, so they render plain. */
const PLAIN_PARAGRAPHS = new Set(["intro"]);

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const CategorizerDisclosureModal = ({
  open,
  mode = "gate",
  onCancel,
  onAccept,
  saving = false,
  error = null,
}) => {
  const readOnly = mode === "review";
  const [checked, setChecked] = useState(false);
  const dialogRef = useRef(null);
  const headingId = `${useId()}-categorizer-disclosure`;

  // Each opening starts from an unticked box — a previous session's tick
  // must never carry over into a fresh acknowledgement.
  useEffect(() => {
    if (open) setChecked(false);
  }, [open, mode]);

  // Focus moves to the modal itself on open. Returning focus to the button
  // that opened it is the caller's job (it knows which one that was).
  useEffect(() => {
    if (!open) return;
    dialogRef.current?.focus();
  }, [open]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel?.();
        return;
      }
      if (e.key !== "Tab") return;

      // Keep Tab inside the dialog while it is modal.
      const root = dialogRef.current;
      if (!root) return;
      const nodes = Array.from(root.querySelectorAll(FOCUSABLE));
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;

      if (!root.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && (active === first || active === root)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onCancel]
  );

  useEffect(() => {
    if (!open) return undefined;
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
      <div
        className="absolute inset-0 bg-[rgba(0,0,0,.6)] backdrop-blur-[5px]"
        onClick={onCancel}
        aria-hidden="true"
        data-testid="categorizer-disclosure-backdrop"
      />

      {/* Column layout: only the prose scrolls, so the checkbox and the
          buttons stay on screen at 375×667. */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        className="relative flex flex-col w-full max-w-lg max-h-[calc(100vh-1.5rem)] sm:max-h-[calc(100vh-2rem)] bg-arc-card rounded-arc-card ring-1 ring-arc-line shadow-arc-lg focus:outline-none"
      >
        {/* Header */}
        <div className="flex items-start gap-3 px-5 sm:px-[22px] pt-5 pb-3 border-b border-arc-line">
          <div className="w-9 h-9 rounded-arc-ctl bg-arc-accent-soft ring-1 ring-arc-line flex items-center justify-center flex-shrink-0">
            <ShieldCheck size={17} className="text-arc-accent-ink" />
          </div>
          {/* No corner ✕: ARC-15 enumerates exactly three ways out of the gate
              — Cancel, Escape, backdrop — and exactly one button in review. */}
          <h2 id={headingId} className="flex-1 min-w-0 text-h2 text-arc-ink pt-0.5">
            {CATEGORIZER_DISCLOSURE_HEADING}
          </h2>
        </div>

        {/* Scrollable prose */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-[22px] py-4 space-y-3">
          {CATEGORIZER_DISCLOSURE_PARAGRAPHS.map((p) => (
            <p key={p.id} className="text-sm text-arc-ink-2 leading-relaxed">
              {PLAIN_PARAGRAPHS.has(p.id) ? (
                p.lead
              ) : (
                <strong className="font-semibold text-arc-ink">{p.lead}</strong>
              )}
              {p.rest ? ` ${p.rest}` : null}
            </p>
          ))}
        </div>

        {/* Footer — never scrolls away */}
        <div className="flex-shrink-0 border-t border-arc-line px-5 sm:px-[22px] py-4 space-y-3">
          {!readOnly && (
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                disabled={saving}
                className="mt-0.5 w-4 h-4 flex-shrink-0 accent-arc-accent cursor-pointer"
              />
              <span className="text-sm text-arc-ink">
                {CATEGORIZER_DISCLOSURE_CHECKBOX_LABEL}
              </span>
            </label>
          )}

          {error && (
            <div
              role="alert"
              className="text-xs px-3 py-2 rounded-arc-ctl ring-1 ring-arc-neg-soft bg-arc-neg-soft text-arc-neg"
            >
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2">
            {readOnly ? (
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 bg-arc-card text-arc-ink text-sm font-semibold ring-1 ring-arc-line rounded-arc-ctl transition hover:bg-arc-paper-2"
              >
                Close
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-4 py-2 bg-arc-card text-arc-ink text-sm font-semibold ring-1 ring-arc-line rounded-arc-ctl transition hover:bg-arc-paper-2"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => onAccept?.()}
                  disabled={!checked || saving}
                  className={`px-4 py-2 text-sm font-semibold rounded-arc-ctl transition ${
                    checked && !saving
                      ? "bg-arc-accent text-white hover:brightness-105 active:brightness-95"
                      : "bg-arc-paper-2 text-arc-ink-4 ring-1 ring-arc-line cursor-not-allowed"
                  }`}
                >
                  {saving ? "Saving…" : "Continue"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategorizerDisclosureModal;
